import React, { useState } from "react";
import { Link } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Lock, Loader2, Eye, EyeOff, Check } from "lucide-react";
import AuthTabs from "@/components/auth/AuthTabs";
import { stampLoginVersion } from "@/lib/loginVersion";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const next = params.get("next");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await base44.auth.loginViaEmailPassword(email, password);
      await stampLoginVersion().catch(() => {});

      // Set customer_data immediately so getCartSessionId() returns basket_<id>
      // on the next page load — prevents guest cart from appearing "wiped"
      try {
        const loggedInUser = await base44.auth.me();
        if (loggedInUser?.square_customer_id) {
          const oldGuest = localStorage.getItem('cart_session_id');
          if (oldGuest && !oldGuest.startsWith('basket_')) {
            localStorage.setItem('pending_cart_migration', oldGuest);
          }
          localStorage.setItem('customer_data', JSON.stringify({
            square_customer_id: loggedInUser.square_customer_id,
            loyalty_account_id: loggedInUser.loyalty_account_id || null
          }));
        }
      } catch (_) { /* non-critical */ }

      // Use hard redirect to reload app with new auth state
      // On iOS WebView, force a full reload after navigation to prevent the JS module singleton
      // from being reused without the newly stored auth token
      // Only allow same-origin, single-slash paths — "//evil.com" and "/\evil.com"
      // are protocol-relative and would send the user off-site after login.
      const isSafeNext = typeof next === 'string' && /^\/(?![/\\])/.test(next);
      const destination = isSafeNext ? next : '/Order';
      setSuccess(true);
      // Warm the Order page's code into the browser cache during the tick animation
      import('@/pages/Order').catch(() => {});
      setTimeout(() => { window.location.href = destination; }, 800);
    } catch (err) {
      setError(err.message || err.toString() || "Login failed. Please check your connection and try again.");
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col">
      <AuthTabs active="login" />
      <h1 className="text-2xl font-bold text-gray-900 text-center mb-2" style={{ fontFamily: 'Georgia, serif' }}>Welcome back</h1>
      <p className="text-gray-500 text-center mb-6 text-sm">Sign in to your account</p>
      {params.get("reset") === "admin" ? (
        <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-700 text-sm border border-green-100">
          Everyone has been signed out (now v{params.get("v")}): {params.get("s")} sessions and {params.get("c")} verification codes cleared. Please sign in again.
        </div>
      ) : params.get("reset") && (
        <div className="mb-4 p-3 rounded-lg bg-blue-50 text-blue-800 text-sm border border-blue-100">
          Your session was reset — please sign in again. Your basket has been kept.
        </div>
      )}
      {error && (
        <div key={error} className="mb-4 p-3 rounded-lg bg-red-50 text-red-600 text-sm border border-red-100 animate-shake">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2 animate-fade-up [animation-delay:450ms]">
          <Label htmlFor="email" className="text-gray-700 font-medium">Email</Label>
          <div className="relative group">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-all duration-300 group-focus-within:text-[#1A2744] group-focus-within:translate-x-0.5 group-focus-within:scale-110" aria-hidden="true" />
            <Input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10 h-12 rounded-full border-gray-300 transition-shadow duration-300 focus:border-[#1A2744] focus-visible:ring-4 focus-visible:ring-[#1A2744]/15"
              required
            />
          </div>
        </div>
        <div className="space-y-2 animate-fade-up [animation-delay:550ms]">
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-gray-700 font-medium">Password</Label>
            <button
              type="button"
              onClick={() => window.open('https://www.peonyhospitalityltd.co.uk/forgot-password', '_system')}
              className="text-xs font-medium hover:underline bg-transparent border-0 p-0 cursor-pointer"
              style={{ color: '#1A2744' }}
            >
              Forgot password?
            </button>
          </div>
          <div className="relative group">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-all duration-300 group-focus-within:text-[#1A2744] group-focus-within:translate-x-0.5 group-focus-within:scale-110" aria-hidden="true" />
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-10 pr-10 h-12 rounded-full border-gray-300 transition-shadow duration-300 focus:border-[#1A2744] focus-visible:ring-4 focus-visible:ring-[#1A2744]/15"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors bg-transparent border-0 p-0 cursor-pointer"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div className="animate-fade-up [animation-delay:650ms]">
          <button
            type="submit"
            disabled={loading}
            aria-label={loading ? "Logging in" : undefined}
            className={`mx-auto h-12 text-white rounded-full text-base font-semibold transition-all duration-500 ease-out hover:opacity-90 active:scale-95 flex items-center justify-center gap-2 ${loading || success ? 'w-12' : 'w-full'}`}
            style={{ backgroundColor: '#1A2744' }}
          >
            {success ? <Check className="w-6 h-6 animate-pop" strokeWidth={3} /> : loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Log in"}
          </button>
        </div>
      </form>
      <div className="text-center text-sm text-gray-500 mt-auto pt-6 space-y-3">
        <p className="text-xs text-gray-400 leading-relaxed">
          Sign in to earn loyalty points, track orders and access exclusive rewards.
        </p>
        <Link to="/HowToRegister" className="text-sm hover:underline block" style={{ color: '#1A2744' }}>
          Need help? How to register
        </Link>
        <Link to="/" className="text-sm text-gray-400 hover:text-gray-600 underline underline-offset-2 inline-block">
          Return to Home
        </Link>
      </div>

    </div>
  );
}